import { Skeleton } from '@mantine/core'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { cn } from 'src/utils'

import type { StageCount } from '../lib/prospectCounts'

import WidgetShell from './WidgetShell'

/**
 * The prospects strip: how many people are in each hiring stage, and how many
 * reached each terminal outcome this month. Every number is a link into a
 * filtered Applicants list — there are no actions on this widget.
 *
 * Takes plain counts, so Storybook covers every state without Apollo.
 */
export interface ProspectsWidgetViewProps {
  /** The four hiring stages, already zero-filled and in canonical order. */
  lanes: StageCount[]
  /** The four terminal outcomes for the period, in canonical order. */
  outcomes: StageCount[]
  total: number
  loading?: boolean
  /**
   * False when there is no employee to scope to. The widget then says so rather
   * than rendering zeros, which would read as "this company has no prospects".
   */
  isScoped?: boolean
  /** True when a query failed. One line, because a read-only strip offers no fix. */
  hasError?: boolean
  onOpenLane?: (stage: string) => void
  onOpenOutcome?: (stage: string) => void
}

/** Wide enough for five digits so a big account doesn't shift the label. */
const COUNT_SKELETON_WIDTH = 34

const LaneButton = ({
  lane,
  loading,
  onOpen,
}: {
  lane: StageCount
  loading: boolean
  onOpen?: (stage: string) => void
}) => (
  <button
    type="button"
    // A computed name would be "148 Lead", which is a brittle thing to assert
    // on and a strange thing to hear read aloud.
    aria-label={`${lane.label}: ${lane.count}`}
    disabled={loading}
    onClick={() => onOpen?.(lane.stage)}
    className={cn(
      'flex min-w-0 flex-1 flex-row items-center gap-2 rounded px-3 py-2 text-left',
      !loading && 'hover:bg-doubleNickel-brand-25'
    )}
  >
    {loading ? (
      <Skeleton height={18} width={COUNT_SKELETON_WIDTH} />
    ) : (
      <span
        className={cn(
          'flex-shrink-0 text-base font-semibold text-doubleNickel-gray-900',
          // Zero is shown, never hidden — but it is dimmed, because a lane with
          // nobody in it is a fact rather than something to look at.
          lane.count === 0 && 'text-doubleNickel-gray-400'
        )}
      >
        {lane.count}
      </span>
    )}
    <span className="min-w-0 truncate text-xs font-medium text-doubleNickel-gray-700">
      {lane.label}
    </span>
  </button>
)

const OutcomeButton = ({
  outcome,
  loading,
  onOpen,
}: {
  outcome: StageCount
  loading: boolean
  onOpen?: (stage: string) => void
}) => (
  <button
    type="button"
    aria-label={`${outcome.label} this month: ${outcome.count}`}
    disabled={loading}
    onClick={() => onOpen?.(outcome.stage)}
    className={cn(
      'flex flex-shrink-0 flex-row items-baseline gap-1 rounded px-2 py-1',
      !loading && 'hover:bg-doubleNickel-brand-25'
    )}
  >
    {loading ? (
      <Skeleton height={14} width={22} />
    ) : (
      <span
        className={cn(
          'text-xs font-semibold',
          outcome.count === 0
            ? 'text-doubleNickel-gray-400'
            : // Hired is the only coloured number on the widget. The off-ramps
            // stay grey on purpose — disqualifying someone for a real reason
            // is the system working, and red would scold the recruiter.
            outcome.stage === ApplicantStage.HIRED
            ? 'text-doubleNickel-success-700'
            : 'text-doubleNickel-gray-900'
        )}
      >
        {outcome.count}
      </span>
    )}
    <span className="whitespace-nowrap text-[11px] text-doubleNickel-gray-600">
      {outcome.label}
    </span>
  </button>
)

const ProspectsWidgetView = ({
  lanes,
  outcomes,
  total,
  loading = false,
  isScoped = true,
  hasError = false,
  onOpenLane,
  onOpenOutcome,
}: ProspectsWidgetViewProps) => (
  <WidgetShell
    fillHeight={false}
    title={
      <div className="flex min-w-0 flex-row items-baseline gap-2">
        <span className="min-w-0 truncate text-base font-medium text-doubleNickel-gray-800">
          Prospects
        </span>
        {loading ? (
          <Skeleton height={12} width={COUNT_SKELETON_WIDTH} />
        ) : (
          isScoped && (
            <span className="flex-shrink-0 text-[11px] font-medium text-doubleNickel-gray-500">
              {total}
            </span>
          )
        )}
      </div>
    }
    controls={
      isScoped && (
        <div className="flex flex-shrink-0 flex-row items-center gap-1">
          {/* The lanes are live totals but the outcomes are windowed, and they
              sit in the same band at the same size. Without this the four
              outcome numbers read as lifetime. */}
          <span className="mr-1 whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-doubleNickel-gray-400">
            This month
          </span>
          {outcomes.map((outcome) => (
            <OutcomeButton
              key={outcome.stage}
              outcome={outcome}
              loading={loading}
              onOpen={onOpenOutcome}
            />
          ))}
        </div>
      )
    }
  >
    {!isScoped ? (
      <div className="px-3 py-2 text-xs text-doubleNickel-gray-500">
        Pick whose work to show to see their prospects.
      </div>
    ) : hasError ? (
      <div className="px-3 py-2 text-xs text-doubleNickel-gray-500">
        Couldn&apos;t load these counts. Refresh to try again.
      </div>
    ) : (
      <div className="flex flex-row items-stretch divide-x divide-doubleNickel-gray-100">
        {lanes.map((lane) => (
          <LaneButton
            key={lane.stage}
            lane={lane}
            loading={loading}
            onOpen={onOpenLane}
          />
        ))}
      </div>
    )}
  </WidgetShell>
)

export default ProspectsWidgetView
